import React, { useCallback, useMemo } from 'react';
import {
  RadioButtonGroup,
  RadioButtonGroupOption,
} from '../../../../components/RadioButtonGroup/RadioButtonGroup';
import { TextField } from '../../../../components/TextField/TextField';
import { useCompleteUserInfoPrivateValidationForm } from '../../../../hooks/validations';
import { useLanguage } from '../../../../locale/useLanguage';
import { Gender } from '../../../../models/user.models';
import citizenships from '../../../../assets/citizenships.json';
import { CalendarIcon } from '../../../../components/Icons/Calendar';
import { SelectField } from '../../../../components/SelectField/SelectField';
import { Citizeship } from '../../../../models';

type PrivateFormValidators = Omit<
  ReturnType<typeof useCompleteUserInfoPrivateValidationForm>,
  'handleSubmit'
>;

type PrivateFormProps = {
  validators: PrivateFormValidators;
  countries?: Array<string>;
  states?: Array<string>;
  cities?: Array<string>;
  postalCodes?: Array<string>;
};

export const PrivateForm: React.FC<PrivateFormProps> = ({
  validators,
  cities,
  states,
  postalCodes,
}) => {
  const [t] = useLanguage();

  const {
    citizenshipValidator,
    dateOfBirthValidator,
    errors,
    firstNameValidator,
    genderField,
    genderFieldState,
    lastNameValidator,
    placeOfBirthValidator,
    taxCodeValidator,
    residentialAddressValidator,
    countryCodeValidator,
    municipalityValidator,
    municipalityFieldState,
    postCodeValidator,
    provinceValidator,
    calculateTaxCode: calculateTaxCodeFromForm,
    setValue,
    watchCountryCode,
  } = validators;

  const sexOptions: RadioButtonGroupOption<Gender>[] = useMemo(
    () => [
      {
        label: t('completeUserInfoView.man'),
        value: Gender.Male,
      },
      {
        label: t('completeUserInfoView.woman'),
        value: Gender.Female,
      },
    ],
    [t]
  );

  const selectValueDisplayFn = useCallback((v: string) => v, []);

  const citizenshipSelectDisplayFn = useCallback(
    (c: Citizeship) => c.itName,
    []
  );
  const citizenshipSelectValueFn = useCallback(
    (c: Citizeship) => c.isoAlpha2,
    []
  );

  const calculateTaxCode = useCallback(() => {
    const calculatedTaxCode = calculateTaxCodeFromForm();
    setValue('fiscalCode', calculatedTaxCode || '');
  }, [calculateTaxCodeFromForm, setValue]);

  return (
    <>
      <div className="complete-profile--field-container">
        <TextField
          {...firstNameValidator}
          placeholder={t('completeUserInfoView.firstName')}
          error={errors.name?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...lastNameValidator}
          placeholder={t('completeUserInfoView.lastName')}
          error={errors.surname?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <label className="complete-profile--label-style ">
          {t('completeUserInfoView.sex')}
        </label>
        <RadioButtonGroup
          options={sexOptions}
          selectedValue={genderField.value}
          onChangeValue={genderField.onChange}
        />
        <p className="complete-profile--sex-error">
          {genderFieldState.error?.message}
        </p>
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...placeOfBirthValidator}
          placeholder={t('completeUserInfoView.placeOfBirth')}
          error={errors.placeOfBirth?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...dateOfBirthValidator}
          type="date"
          Icon={CalendarIcon}
          placeholder={t('completeUserInfoView.dateOfBirth')}
          error={
            errors.dateOfBirth?.type === 'validate'
              ? t('completeUserInfoView.dateOfBirthNotValid')
              : undefined
          }
        />
      </div>
      <div className="complete-profile--field-container">
        <SelectField
          {...citizenshipValidator}
          options={citizenships}
          placeholder={t('completeUserInfoView.citizenship')}
          displayFn={citizenshipSelectDisplayFn}
          valueFn={citizenshipSelectValueFn}
          error={errors.citizenship?.message}
        />
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...taxCodeValidator}
          placeholder={t('completeUserInfoView.taxCode')}
          error={errors.fiscalCode?.message}
        />
        <button
          onClick={calculateTaxCode}
          className="link complete-profile--calc-now"
        >
          {t('completeUserInfoView.calculateTaxCode')}
        </button>
      </div>
      <div className="complete-profile--billing-address">
        {t('completeUserInfoView.billingAddress')}:
      </div>
      <div className="complete-profile--field-container">
        <SelectField
          {...countryCodeValidator}
          options={citizenships || []}
          placeholder={t('completeUserInfoView.state')}
          displayFn={citizenshipSelectDisplayFn}
          valueFn={citizenshipSelectValueFn}
          error={errors.countryCode?.message}
        />
      </div>
      {watchCountryCode === 'IT' && (
        <div className="complete-profile--field-container">
          <SelectField
            {...provinceValidator}
            options={states || []}
            placeholder={t('completeUserInfoView.province')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.province?.message}
          />
        </div>
      )}
      <div className="complete-profile--field-container">
        {watchCountryCode === 'IT' ? (
          <SelectField
            {...municipalityValidator}
            options={cities || []}
            placeholder={t('completeUserInfoView.municipality')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.municipality?.message}
          />
        ) : (
          <TextField
            value={municipalityValidator.value}
            name={municipalityValidator.name}
            onChange={municipalityValidator.onChange}
            placeholder={t('completeUserInfoView.municipality')}
            error={municipalityFieldState.error?.message}
          />
        )}
      </div>
      <div className="complete-profile--field-container">
        {watchCountryCode === 'IT' ? (
          <SelectField
            {...postCodeValidator}
            options={postalCodes || []}
            placeholder={t('completeUserInfoView.zipCode')}
            displayFn={selectValueDisplayFn}
            valueFn={selectValueDisplayFn}
            error={errors.postcode?.message}
          />
        ) : (
          <TextField
            {...postCodeValidator}
            placeholder={t('completeUserInfoView.zipCode')}
            error={errors.postcode?.message}
          />
        )}
      </div>
      <div className="complete-profile--field-container">
        <TextField
          {...residentialAddressValidator}
          placeholder={t('completeUserInfoView.billingAddress')}
          error={errors.residentialAddress?.message}
        />
      </div>
    </>
  );
};
